import { AppBar, Box, Tab, Tabs, useMediaQuery, useTheme } from '@material-ui/core';
import { Helmet } from 'react-helmet';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Layout } from '../../../components/ui/Layout';
import { setActiveInvoice } from '../../../redux/actions/sales';
import SwipeableViews from 'react-swipeable-views';
import PropTypes from 'prop-types';
import { Spinner } from '../../../components/ui/Spinner';
import { Action, Fab } from 'react-tiny-fab';
import { Add, Create, CreateRounded } from '@material-ui/icons';
import { actionAddFreeOrderLineToOrder, actionAddOrderLineToOrder } from '../../../redux/actions/actions';
import { TransitionsModal } from '../../../components/ui/TransitionModal';
import { ROLREVENDEDOR, ROLADMIN } from '../../../utils/constants';
import { startGatheringDataFromServicesReseller } from '../../../redux/actions/supplierServices';
import { startLogOutCleanSuppliers } from '../../../redux/actions/suppliers';
import { startLogOutActiveRate } from '../../../redux/actions/exchangeRate';
import { navigate, useParams } from '@reach/router';
import { Detail } from '../../../components/invoices/detail/Detail';
import { Info } from '../../../components/invoices/detail/Info';
import { EstadoVenta } from '../../../components/invoices/detail/EstadoVenta';
import { startLoadingComisionistas } from '../../../redux/actions/comisionistas';
import { setActiveUserState } from '../../../redux/actions/users';
import { FacturasTable } from '@/components/invoices/detail/facturas/FacturasTable';
import { getFacturas } from '@/redux/actions/facturasService';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

const SaleDetailScreen = () => {
  const theme = useTheme();
  const matchesXS = useMediaQuery(theme.breakpoints.down('xs'));
  const matchesMD = useMediaQuery(theme.breakpoints.down('md'));

  const dispatch = useDispatch();
  const params = useParams();

  const { menuState, loading } = useSelector(state => state.ui);
  const { activeEdit } = useSelector(state => state.invoice);
  const { modal } = useSelector(state => state.modal);
  const { route } = useSelector(state => state.route);
  const { idRol, id: loggedInUser, idAgencia } = useSelector(state => state.auth);
  const { facturas } = useSelector(state => state.factura);

  const [value, setValue] = useState(0);

  const currentSale = idRol === ROLREVENDEDOR ? { ...activeEdit, esNuevaVenta: activeEdit?.OrigenB2B } : activeEdit;

  const hasFacturas = facturas?.length > 0;
  const PkVentas = params?.PkVentas;

  const tabs = useMemo(() => {
    const list = [
      {
        key: 'venta',
        label: 'Venta',
        render: <Detail showActionButtons={!currentSale?.esNuevaVenta} />,
      },
      {
        key: 'info',
        label: 'Info',
        render: <Info showActionButtons={!currentSale?.esNuevaVenta} />,
      },
    ];

    if (!currentSale?.esNuevaVenta) {
      list.push({
        key: 'estado',
        label: 'Estado',
        render: <EstadoVenta />,
      });
    }

    if (hasFacturas && idRol !== ROLREVENDEDOR) {
      list.push({
        key: 'facturas',
        label: 'Facturas',
        render: <FacturasTable PkVentas={PkVentas} />,
      });
    }

    return list;
  }, [currentSale?.esNuevaVenta, hasFacturas, PkVentas, idRol]);

  useEffect(() => {
    if (value >= tabs.length) {
      setValue(0);
    }
  }, [tabs.length, value]);

  const handleChange = (_, newValue) => setValue(newValue);
  const handleChangeIndex = index => setValue(index);

  const handleAddRowService = () => dispatch(actionAddOrderLineToOrder());
  const handleAddRowCustomService = () => dispatch(actionAddFreeOrderLineToOrder());

  useEffect(() => {
    dispatch(setActiveUserState(loggedInUser));
  }, [dispatch, loggedInUser]);

  useEffect(() => {
    dispatch(startLoadingComisionistas());
  }, [dispatch]);

  useEffect(() => {
    dispatch(setActiveInvoice(PkVentas));
  }, [dispatch, PkVentas]);

  useEffect(() => {
    if (route) navigate(route);
  }, [route]);

  useEffect(() => {
    if (idRol === ROLREVENDEDOR && modal) {
      dispatch(startGatheringDataFromServicesReseller(idAgencia > 0 ? idAgencia : loggedInUser));
    }
  }, [idRol, modal, loggedInUser, idAgencia, dispatch]);

  useEffect(() => {
    if (!modal) {
      dispatch(startLogOutCleanSuppliers());
      dispatch(startLogOutActiveRate());
    }
  }, [modal, dispatch]);

  useEffect(() => {
    dispatch(getFacturas(PkVentas));
  }, [PkVentas, dispatch]);

  return (
    <Layout>
      <Helmet>
        <title>Venta en Detalle | Xplora</title>
      </Helmet>

      {loading && !modal ? (
        <Spinner />
      ) : (
        <>
          <Box
            sx={{
              width: matchesXS ? '85vw' : matchesMD ? (menuState ? '55vw' : '85vw') : menuState ? '82vw' : '91vw',
              mt: 4,
              bgcolor: 'background.paper',
            }}>
            <AppBar position="static" color="transparent">
              <Tabs value={value} onChange={handleChange} variant="fullWidth">
                {tabs.map(tab => (
                  <Tab key={tab.key} label={tab.label} />
                ))}
              </Tabs>
            </AppBar>

            <SwipeableViews
              axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
              index={value}
              onChangeIndex={handleChangeIndex}>
              {tabs.map((tab, index) => (
                <TabPanel key={tab.key} value={value} index={index}>
                  {tab.render}
                </TabPanel>
              ))}
            </SwipeableViews>
          </Box>

          {!currentSale?.FechaFactura && !modal && !currentSale?.esNuevaVenta && !hasFacturas && (
            <Fab icon={<Add />} alwaysShowTitle>
              <Action text="Nueva Línea de Venta" onClick={handleAddRowService}>
                <Create />
              </Action>

              {idRol === ROLADMIN && (
                <Action text="Nueva Línea de Venta Libre" onClick={handleAddRowCustomService}>
                  <CreateRounded />
                </Action>
              )}
            </Fab>
          )}
        </>
      )}

      <TransitionsModal />
    </Layout>
  );
};

export default SaleDetailScreen;
